import React, { useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Link,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import darkLogo from "../assets/images/Newlogo-black.png";
import BackToTopButton from "../components/common/BackToTopButton";
import Footer from "./Footer";
import JoinCommunitySection from "./common/JoinCommunitySection";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  // ⬇️ Scroll to the very top every time this page mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacyverklaring&nbsp;| NOMAD360</title>
      </Helmet>

      <Box position="absolute" top={0} left={0} width="100%" zIndex="2">
        {/* Navbar */}
        <Navbar
          bgColor="white"
          linkColor="black"
          styling="outline"
          logoSrc={darkLogo}
          languageCodeColor="black"
          userProfileBgColor="transparent"
        />

        <Box
          maxW={{ base: "100vw", sm: "85vw", md: "85vw", lg: "60vw" }}
          mx="auto"
          px={4}
          py={8}
          pt={8}
        >
          {/* Title */}
          <Heading as="h1" size="xl" align="center" mt={10} mb={10}>
            Privacyverklaring NOMAD360
          </Heading>
          <VStack align="start" spacing={4}>
            <Text mb={0}>
              Dit is de privacyverklaring (hierna ook "Verklaring" genoemd) van
              NOMAD360, een dienst die wordt aangeboden door Lancewood AI
              Solutions B.V. In deze Verklaring leggen we op een eenvoudige en
              begrijpelijke manier uit wie we zijn, welke persoonsgegevens we
              over u verwerken, waarom we deze gegevens verzamelen en wat we
              ermee doen. Onder persoonsgegevens verstaan we alle informatie en
              verzamelingen van gegevens waarmee wij u direct of indirect kunnen
              identificeren, zoals uw voor- en achternaam, e-mailadres,
              geboortedatum, enz.
            </Text>

            <Text mb={0}>
              Wij kunnen deze verklaring van tijd tot tijd wijzigen. De meest
              actuele versie van deze Verklaring is altijd te vinden op onze
              website:{" "}
              <Link
                color="blue.500"
                href="https://nomad360.io/policies/privacy-policy/nl"
              >
                nomad360.io/policies/privacy-policy/nl
              </Link>
              . We raden u aan deze Verklaring regelmatig te raadplegen om op de
              hoogte te blijven van eventuele wijzigingen.
            </Text>

            <Text>
              Deze Verklaring is voor het laatst bijgewerkt op: 14 april 2025
            </Text>

            {/* Who are we? */}
            <Heading as="h2" size="lg" mt={0}>
              Wie zijn wij?
            </Heading>
            <Text>
              NOMAD360 is een platform waarmee gebruikers, met behulp van een
              AI-model, gepersonaliseerde reisadviezen en andere content kunnen
              genereren op basis van informatie die zij zelf aanleveren, zoals
              reisvoorkeuren en bestemmingsdetails. NOMAD360 wordt aangeboden
              door Lancewood AI Solutions B.V. In deze Verklaring verwijzen
              "wij", "ons" of "NOMAD360" naar de verwerkingsverantwoordelijken
              van uw persoonsgegevens. Verwijzingen naar NOMAD360 verwijzen
              specifiek naar Lancewood AI Solutions B.V. (Kamer van
              Koophandel-nummer: 95481214 ).
            </Text>

            {/* When does this Statement apply? */}
            <Heading as="h2" size="lg">
              Wanneer is deze Verklaring van toepassing?
            </Heading>
            <Text mb={0}>
              Deze Verklaring beschrijft hoe wij uw persoonsgegevens verzamelen
              en gebruiken, bijvoorbeeld wanneer u:
            </Text>
            <VStack align="start" pl={6}>
              <Text mb={0}>• De website van NOMAD360 bezoekt;</Text>
              <Text mb={0}>• Onze sociale media-kanalen gebruikt;</Text>
              <Text mb={0}>• Een account hebt op NOMAD360;</Text>
              <Text mb={0}>
                • Gebruikmaakt van de diensten of functionaliteiten van
                NOMAD360;
              </Text>
              <Text mb={0}>
                • Om welke reden dan ook contact met ons opneemt.
              </Text>
            </VStack>

            {/* What personal data do we process, and why? */}
            <Heading as="h2" size="lg">
              Welke persoonsgegevens verwerken wij, en waarom?
            </Heading>

            {/* 1. Persoonsgegevens account */}
            <Heading as="h3" size="md">
              1. Persoonsgegevens van personen met een account voor NOMAD360
            </Heading>
            <Text>
              Wanneer u een account aanmaakt op NOMAD360, verzamelen wij de
              volgende persoonsgegevens:
            </Text>

            {/* Table: categories & examples */}
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Categorieën van persoonsgegevens</Th>
                  <Th>Voorbeelden</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>a) Contactgegevens</Td>
                  <Td>E-mailadres, voor- en achternaam</Td>
                </Tr>
                <Tr>
                  <Td>b) Inloggegevens</Td>
                  <Td>E-mailadres, wachtwoord, of Google-account</Td>
                </Tr>
                <Tr>
                  <Td>c) Leeftijd</Td>
                  <Td>Wel of niet 16 jaar of ouder</Td>
                </Tr>
                <Tr>
                  <Td>d) Instellingen en voorkeuren</Td>
                  <Td>Standaard land, taal, valuta, (reis)voorkeuren</Td>
                </Tr>
                <Tr>
                  <Td>e) Gebruikerslocatie</Td>
                  <Td>
                    IP-adres, locatiegegevens afgeleid van GPS of andere
                    technologieën
                  </Td>
                </Tr>
                <Tr>
                  <Td>f) Activiteit binnen de social community</Td>
                  <Td>
                    Gebruikersprofielen, berichten en interacties binnen de
                    community
                  </Td>
                </Tr>
                <Tr>
                  <Td>g) Financiële gegevens</Td>
                  <Td>
                    Bank- en betaalgegevens, ingevoerde financiële informatie
                    voor de "budget planner" binnen NOMAD360
                  </Td>
                </Tr>
              </Tbody>
            </Table>

            <Text>
              Doeleinden en wettelijke grondslag waarop wij de bovengenoemde
              categorieën van persoonsgegevens van contactpersonen verwerken:
            </Text>

            {/* Table: purposes & legal basis */}
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Doel</Th>
                  <Th>Wettelijke grondslag</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>
                    a) Het onderhouden van contact en communiceren over vragen
                    of andere informatie via e-mail
                  </Td>
                  <Td>
                    Noodzakelijk voor de uitvoering van de overeenkomst tussen u
                    en Lancewood AI Solutions; artikel 6(1)(b) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    U benaderen voor commerciële aanbiedingen, nieuwsbrieven en
                    promoties/acties die voor u interessant kunnen zijn
                  </Td>
                  <Td>
                    Gerechtvaardigd belang van Lancewood AI Solutions om
                    marketing te versturen naar bestaande gebruikers; artikel
                    6(1)(f) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    Het terugkoppelen van, en u contacteren naar aanleiding van,
                    ingevulde feedbackformulieren
                  </Td>
                  <Td>Uw toestemming; artikel 6(1)(a) AVG</Td>
                </Tr>
                <Tr>
                  <Td>
                    b) Het aanmaken van een gebruikersnaam en account en toegang
                    verschaffen tot NOMAD360
                  </Td>
                  <Td>
                    Noodzakelijk voor de uitvoering van de overeenkomst tussen u
                    en Lancewood AI Solutions; artikel 6(1)(b) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    c) Het beoordelen of u de leeftijd van 16 jaar hebt bereikt
                  </Td>
                  <Td>
                    Noodzakelijk om te voldoen aan wettelijke verplichtingen
                    onder artikel 8 AVG; artikel 6(1)(c) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    d) Het afstemmen van onze diensten op uw voorkeuren en
                    gebruikservaring
                  </Td>
                  <Td>Uw toestemming; artikel 6(1)(a) AVG </Td>
                </Tr>
                <Tr>
                  <Td>
                    e) Het gebruiken van locatiegegevens om specifieke
                    functionaliteiten binnen NOMAD360 te activeren, zoals het
                    aanbieden van content of diensten die relevant zijn voor uw
                    locatie
                  </Td>
                  <Td>Uw toestemming; artikel 6(1)(a) AVG </Td>
                </Tr>
                <Tr>
                  <Td>
                    f) Het beheren van de social community binnen NOMAD360
                  </Td>
                  <Td>
                    Noodzakelijk voor de uitvoering van de overeenkomst tussen u
                    en Lancewood AI Solutions; artikel 6(1)(b) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>g) Het verwerken van betalingen of terugbetalingen </Td>
                  <Td>
                    Noodzakelijk voor de uitvoering van de overeenkomst tussen u
                    en Lancewood AI Solutions; artikel 6(1)(b) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    Het opslaan van financiële gegevens om gebruikers in staat
                    te stellen hun uitgaven te monitoren en budgetten op te
                    stellen in de “budget planner”
                  </Td>
                  <Td>
                    Noodzakelijk voor de uitvoering van de overeenkomst tussen u
                    en Lancewood AI Solutions; artikel 6(1)(b) AVG
                  </Td>
                </Tr>
              </Tbody>
            </Table>

            <Text>
              Als u niet langer commerciële aanbiedingen wenst te ontvangen,
              kunt u uzelf eenvoudig afmelden via de 'uitschrijven'-knop
              onderaan de e-mail of door een e-mail te sturen naar
              info@nomad360.io. Houd er rekening mee dat het enkele dagen kan
              duren voordat uw gegevens volledig uit onze systemen zijn
              verwijderd.
            </Text>

            {/* 2. Personal data of persons using the services */}
            <Heading as="h3" size="md">
              2. Persoonsgegevens van personen die gebruikmaken van de diensten
              en/of functionaliteiten van NOMAD360
            </Heading>
            <Text>
              Wanneer u gebruikmaakt van de diensten van NOMAD360, zoals het
              genereren van gepersonaliseerde reisadviezen of andere content,
              kunnen wij de volgende persoonsgegevens verzamelen:
            </Text>

            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Categorieën van persoonsgegevens</Th>
                  <Th>Voorbeelden</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>a) Gebruikersinhoud</Td>
                  <Td>
                    Geüploade informatie van uzelf die u aanlevert voor het
                    personaliseren van de content, zoals naam, adresgegevens en
                    reisvoorkeuren
                  </Td>
                </Tr>
                <Tr>
                  <Td>b) Gebruikersgegevens</Td>
                  <Td>
                    Klikgedrag, gebruikspatronen binnen de applicatie,
                    interacties binnen de social community, Interessegebieden en
                    voorkeuren afgeleid van uw gebruik van de diensten, IP
                    adres, apparaat gegevens, locatie-informatie en andere
                    technische metadata
                  </Td>
                </Tr>
                <Tr>
                  <Td>c) Financiële gegevens (voor CO2 compensatie)</Td>
                  <Td>
                    Betaalgegevens gerelateerd aan gedane donaties via NOMAD360
                  </Td>
                </Tr>
                <Tr>
                  <Td>d) Wallet-informatie voor NFT verzending</Td>
                  <Td>
                    Crypto-walletadres of, bij afwezigheid hiervan, een
                    e-mailadres voor het aanmaken van een Crossmint-wallet
                  </Td>
                </Tr>
              </Tbody>
            </Table>

            <Text>
              Doeleinden en wettelijke grondslag waarop wij de bovengenoemde
              categorieën van persoonsgegevens van contactpersonen verwerken:
            </Text>

            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Doel</Th>
                  <Th>Wettelijke grondslag </Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>
                    a) Het leveren van gepersonaliseerde content en reisadvies
                  </Td>
                  <Td>
                    Noodzakelijk voor de uitvoering van de overeenkomst tussen u
                    en Lancewood AI Solutions; artikel 6(1)(b) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    b) Het trainen van onze algoritmes, waaronder LLM's en de
                    social community-functionaliteiten, om de kwaliteit en
                    relevantie van onze diensten te verbeteren
                  </Td>
                  <Td>Toestemming van de gebruiker; artikel 6(1)(a) AVG. </Td>
                </Tr>
                <Tr>
                  <Td>
                    Het personaliseren van advertenties en promotionele content{" "}
                  </Td>
                  <Td>Toestemming van de gebruiker; artikel 6(1)(a) AVG. </Td>
                </Tr>
                <Tr>
                  <Td>
                    Het monitoren en analyseren van het gebruik van onze
                    diensten om deze te verbeteren
                  </Td>
                  <Td>Toestemming van de gebruiker; artikel 6(1)(a) AVG. </Td>
                </Tr>
                <Tr>
                  <Td>c) Het verwerken van betalingen of terugbetalingen </Td>
                  <Td>
                    Noodzakelijk voor de uitvoering van de overeenkomst tussen u
                    en Lancewood AI Solutions; artikel 6(1)(b) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    d) Het verzenden van een NFT als aandenken aan de bijdrage{" "}
                  </Td>
                  <Td>
                    Noodzakelijk voor de uitvoering van de overeenkomst tussen u
                    en Lancewood AI Solutions; artikel 6(1)(b) AVG
                  </Td>
                </Tr>
              </Tbody>
            </Table>

            {/* 3. Personal data of persons who contact us */}
            <Heading as="h3" size="md">
              3. Persoonsgegevens van personen die contact met ons opnemen
            </Heading>
            <Text>
              Indien u niet onder een van de bovenstaande categorieën valt, maar
              contact met ons opneemt, bijvoorbeeld via het contactformulier,
              e-mail, telefoon of een van onze sociale mediakanalen, kunnen we
              de volgende persoonsgegevens verzamelen:
            </Text>

            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Categorieën van persoonsgegevens </Th>
                  <Th>Voorbeelden</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>a) Persoonlijke identificatiegegevens</Td>
                  <Td>Voornaam, achternaam </Td>
                </Tr>
                <Tr>
                  <Td>b) Contactgegevens</Td>
                  <Td>E-mail, telefoonnummer </Td>
                </Tr>
                <Tr>
                  <Td>c) Communicatiegegevens</Td>
                  <Td>
                    Inhoud van e-mailberichten, chatberichten of andere gegevens
                    die u met ons deelt
                  </Td>
                </Tr>
              </Tbody>
            </Table>

            <Text>
              Doeleinden en wettelijke grondslag waarop wij de bovengenoemde
              categorieën van persoonsgegevens van contactpersonen verwerken:
            </Text>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Doel</Th>
                  <Th>Wettelijke grondslag</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>a) Het onderhouden van contact en</Td>
                  <Td>
                    Gerechtvaardigd belang van Lancewood AI Solutions om te
                    communiceren met personen die contact opnemen; artikel
                    6(1)(f) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>b) Communiceren over vragen of andere</Td>
                  <Td>
                    Gerechtvaardigd belang van Lancewood AI Solutions om te
                    communiceren met personen die contact opnemen; artikel
                    6(1)(f) AVG
                  </Td>
                </Tr>
                <Tr>
                  <Td>c) Informatie</Td>
                  <Td>
                    Gerechtvaardigd belang van Lancewood AI Solutions om te
                    communiceren met personen die contact opnemen; artikel
                    6(1)(f) AVG
                  </Td>
                </Tr>
              </Tbody>
            </Table>

            {/* Who do we share personal data with? */}
            <Heading as="h2" size="lg" mt={6}>
              Met wie delen wij persoonsgegevens?
            </Heading>
            <Text>
              Wij verkopen uw persoonsgegevens niet aan derden. Ook delen wij uw
              persoonsgegevens niet, behalve met uw toestemming, wanneer dit
              wettelijk verplicht is, of in een beperkt aantal hieronder
              beschreven gevallen. Om de in deze Verklaring genoemde doeleinden
              te realiseren, kunnen wij uw persoonsgegevens delen met de
              volgende entiteiten:
            </Text>
            <VStack align="start" pl={6}>
              <Text mb={0}>
                •{" "}
                <strong>
                  Gelieerde bedrijven binnen Lancewood AI Solutions:
                </strong>{" "}
                vanwege onze organisatiestructuur kunnen uw persoonsgegevens
                worden gedeeld binnen bepaalde gelieerde bedrijven van Lancewood
                AI Solutions, waaronder Lancewood Holding B.V. Alleen
                medewerkers die toegang nodig hebben tot uw informatie, zullen
                toegang tot deze persoonsgegevens hebben.
              </Text>
              <Text mb={0}>
                • <strong>Externe dienstverleners:</strong> sommige
                verwerkingsactiviteiten worden uitbesteed aan betrouwbare derde
                partijen die bepaalde taken namens ons uitvoeren, zoals de
                hosting van onze website, IT-diensten, consultancy,
                bezorgdiensten, betalingsverwerkers, aanbieders van
                elektronische communicatieplatforms en marketingdiensten.
              </Text>
              <Text mb={0}>
                • <strong>Professionele adviseurs en anderen:</strong> uw
                persoonsgegevens kunnen ook worden gedeeld met professionele
                adviseurs, zoals banken, verzekeringsmaatschappijen,
                accountants, advocaten en andere experts.
              </Text>
              <Text mb={0}>
                • <strong>Andere partijen in zakelijke transacties:</strong> uw
                persoonsgegevens kunnen incidenteel worden gedeeld in het kader
                van zakelijke transacties, zoals de verkoop van delen van ons
                bedrijf, of bij reorganisaties, fusies, joint ventures of andere
                vormen van overname van ons bedrijf, activa of aandelen,
                inclusief in geval van faillissement of soortgelijke procedures.
              </Text>
            </VStack>

            <Text>
              Indien persoonsgegevens met een derde partij worden gedeeld,
              zullen wij, waar nodig, een (verwerkers)overeenkomst sluiten met
              deze derde partij die aan alle vereisten van de Algemene
              Verordening Gegevensbescherming (AVG) voldoet.
            </Text>

            {/* How long do we keep personal data? */}
            <Heading as="h2" size="lg">
              Hoe lang bewaren wij persoonsgegevens?
            </Heading>
            <Text mb={0}>
              Wij bewaren uw persoonsgegevens zo lang als nodig is voor de
              doeleinden waarvoor we ze verwerken, tenzij een langere
              bewaartermijn wettelijk verplicht is. Zie de sectie "Welke
              persoonsgegevens verwerken wij, en waarom?" voor meer informatie
              over de verwerkingsdoeleinden.
            </Text>
            <Text mb={0}>
              Onze bewaartermijnen worden bepaald op basis van de volgende
              criteria:
            </Text>
            <VStack align="start" pl={6}>
              <Text mb={0}>
                •{" "}
                <strong>
                  Noodzakelijkheid voor dienstverlening of bedrijfsvoering:
                </strong>{" "}
                hoelang zijn de gegevens nodig om u onze diensten te verlenen of
                onze bedrijfsvoering te ondersteunen?
              </Text>
              <Text mb={0}>
                • <strong>Gesprekken binnen NOMAD360:</strong> gesprekken die u
                voert binnen NOMAD360 worden opgeslagen zolang uw account actief
                is of totdat u zelf besluit het gesprek of uw account te
                verwijderen. Deze gegevens worden veilig opgeslagen op de
                servers van de door ons ingeschakelde hosting-aanbieder en
                worden niet gedeeld met derden.
              </Text>
              <Text mb={0}>
                • <strong>Account op NOMAD360:</strong> zolang het door u
                aangemaakte account actief is, bewaren wij uw accountgegevens.
                Verwijderen van uw account kan eenvoudig via NOMAD360. Indien
                het account 12 maanden inactief is, ontvang u een e-mail met de
                vraag of u het account wilt behouden of verwijderen. Als u
                binnen 2 weken na onze e-mail niet inlogt of ons niet anderszins
                informeert dat u het account wilt behouden, wordt u account
                automatisch verwijderd, met uitzondering van gegevens die wij
                wettelijk moeten bewaren.
              </Text>
              <Text mb={0}>
                • <strong>Wettelijke en contractuele verplichtingen:</strong>{" "}
                soms zijn wij verplicht om gegevens langer te bewaren. Dit kan
                bijvoorbeeld op basis van wetgeving, een overheidsverzoek in
                verband met een onderzoek, of om juridische procedures te
                ondersteunen of ons te beschermen tegen mogelijke claims.
              </Text>
            </VStack>

            {/* Cookies */}
            <Heading as="h2" size="lg">
              Cookies
            </Heading>
            <Text mb={0}>
              Wanneer u onze website bezoekt of gebruikt, verzamelt de website
              automatisch informatie die door uw computer, mobiele telefoon of
              andere apparaten naar ons wordt verzonden Wij maken op onze
              websites gebruik van verschillende soorten cookies en
              vergelijkbare technologieën, waaronder functionele cookies,
              statistiekcookies en marketing/tracking cookies.
            </Text>
            <Text mb={0}>
              In sommige gevallen kunnen cookies persoonsgegevens bevatten,
              zoals een uniek ID dat gekoppeld is aan uw apparaat, IP-adres of,
              wanneer u bent ingelogd, informatie die herleidbaar is tot uw
              account. Marketing- en trackingcookies kunnen bijvoorbeeld
              persoonsgegevens verwerken om gerichte advertenties te tonen en
              gebruikersgedrag te analyseren. Voor deze cookies vragen wij uw
              expliciete toestemming.
            </Text>
            <Text mb={0}>
              Bij uw eerste bezoek aan onze websites verschijnt een pop-up
              waarin u uw cookievoorkeuren kunt instellen. Door op "OK" te
              klikken, geeft u ons toestemming voor het gebruik van cookies. Het
              is via onze cookievoorkeuren-functie op elk moment mogelijk uw
              voorkeuren te wijzigen.
            </Text>
            {/* Social Media */}
            <Heading as="h2" size="lg">
              Welke keuzes hebt u op sociale media?
            </Heading>
            <Text mb={0}>
              Lancewood AI Solutions is actief op sociale mediaplatformen zoals
              TikTok, Instagram, YouTube. Via onze sociale mediapagina's
              ontvangen we bezoekersstatistieken. Hoewel Lancewood AI Solutions
              en de sociale mediaserviceproviders gezamenlijk verantwoordelijk
              zijn voor deze bezoekersstatistieken, is de betreffende sociale
              mediaserviceprovider uw primaire contactpersoon en behandelt deze
              verzoeken om uw rechten uit te oefenen en eventuele klachten. Waar
              nodig, ondersteunen wij de sociale mediaprovider bij het
              beantwoorden van uw verzoeken of klachten.
            </Text>
            <Text mb={0}>
              Voor meer informatie over de persoonsgegevens die wij ontvangen
              van sociale mediaproviders en hoe u uw instellingen kunt
              aanpassen, raadpleeg de websites en privacyverklaringen van de
              betreffende sociale mediaproviders.
            </Text>
            {/* Automated decision-making */}
            <Heading as="h2" size="lg">
              Gebruiken wij geautomatiseerde besluitvorming?
            </Heading>
            <Text mb={0}>
              Wij nemen geen beslissingen die uitsluitend gebaseerd zijn op
              geautomatiseerde verwerking, inclusief profilering, die juridische
              gevolgen hebben of u op een andere manier aanzienlijk beïnvloeden.
              Dit betreft beslissingen die volledig worden genomen door
              computerprogramma's of -systemen, zonder enige menselijke
              tussenkomst (bijvoorbeeld door een van onze medewerkers).
            </Text>

            {/* Outside the EEA */}
            <Heading as="h2" size="lg">
              Verplaatsen wij persoonsgegevens naar landen buiten de EER?
            </Heading>
            <Text mb={0}>
              In principe verwerken wij al uw persoonsgegevens binnen de
              Europese Unie en bewaken wij strikt dat de partijen waarmee we
              samenwerken hetzelfde doen. In uitzonderlijke gevallen kan het
              echter voorkomen dat we persoonsgegevens moeten overdragen naar
              een 'derde' land buiten de Europese Economische Ruimte (EER).
              Mocht een dergelijke situatie zich voordoen en uw persoonsgegevens
              worden overgedragen naar een land met een lager beschermingsniveau
              dan de AVG, dan zullen wij passende waarborgen treffen om uw
              persoonsgegevens veilig over te dragen naar dat derde land,
              bijvoorbeeld door het gebruik van een modelcontract met de
              ontvanger van de persoonsgegevens dat is goedgekeurd door de
              Europese Commissie.
            </Text>

            {/* Security measures */}
            <Heading as="h2" size="lg">
              Welke beveiligingsmaatregelen passen wij toe?
            </Heading>
            <Text mb={0}>
              Om uw persoonsgegevens te beschermen, nemen wij passende
              maatregelen in overeenstemming met de toepasselijke
              gegevensbeveiligingswetten. Dit houdt ook in dat wij van onze
              dienstverleners eisen dat zij passende maatregelen nemen om de
              vertrouwelijkheid en veiligheid van uw gegevens te beschermen.
              Helaas kan geen enkel systeem voor gegevensoverdracht of -opslag
              100% veilig worden gegarandeerd. Afhankelijk van de stand van de
              techniek, de kosten van implementatie en de aard van de te
              beschermen gegevens, nemen wij technische en organisatorische
              maatregelen om risico's zoals vernietiging, verlies, wijziging,
              ongeoorloofde openbaarmaking van of toegang tot uw gegevens te
              voorkomen. Mocht u redenen hebben om aan te nemen dat uw
              interactie met ons of uw gegevens niet meer veilig worden
              behandeld, neem dan onmiddellijk contact met ons op.
            </Text>

            {/* Your responsibilities */}
            <Heading as="h2" size="lg">
              Welke verantwoordelijkheden heeft u?
            </Heading>
            <Text mb={0}>
              We willen u eraan herinneren dat het uw verantwoordelijkheid is om
              ervoor te zorgen dat de gegevens die u aan ons verstrekt, naar
              beste weten juist, volledig en actueel zijn. Daarnaast, als u
              gegevens van anderen met ons deelt, bent u verantwoordelijk voor
              het verzamelen van die gegevens in overeenstemming met de lokale
              wettelijke vereisten. Dit betekent dat u deze personen moet
              informeren over de inhoud van deze Verklaring en hun voorafgaande
              toestemming moet verkrijgen.
            </Text>

            {/* Your rights */}
            <Heading as="h2" size="lg">
              Welke rechten hebt u met betrekking tot uw persoonsgegevens?
            </Heading>
            <Text mb={0}>
              U hebt het recht om toegang te vragen tot uw persoonsgegevens
              (inclusief een kopie), deze te corrigeren en/of te verwijderen, de
              verwerking ervan te beperken, bezwaar te maken tegen de verwerking
              of om overdracht van de door u verstrekte persoonsgegevens te
              verzoeken. Dit kunt u doen door contact met ons op te nemen via de
              onderstaande contactgegevens.
            </Text>
            <Text mb={0}>
              Wij zullen ons best doen om uw verzoek tijdig en kosteloos te
              verwerken, tenzij dit een onevenredige inspanning zou vergen. Om
              misbruik te voorkomen, kunnen we u vragen om uzelf voldoende te
              identificeren voordat we uw verzoek verwerken. Er kunnen
              omstandigheden zijn waarin we niet volledig aan uw verzoek kunnen
              voldoen, bijvoorbeeld vanwege wettelijke bewaartermijnen. Indien
              dergelijke omstandigheden zich voordoen, zullen we u hierover
              informeren. We reageren doorgaans binnen één maand na ontvangst
              van uw verzoek.
            </Text>
            {/* Questions or complaints */}
            <Heading as="h2" size="lg">
              Hebt u vragen, opmerkingen of klachten?
            </Heading>
            <Text mb={0}>
              Als u naar aanleiding van deze Verklaring vragen, opmerkingen of
              klachten hebt, helpen we u graag verder. U kunt ons bereiken via
              e-mail met behulp van de hieronder vermelde contactgegevens.
              Daarnaast heb u het recht om een klacht in te dienen bij de
              Autoriteit Persoonsgegevens of, indien u in een ander land woont
              of werkt dan Nederland, bij de relevante privacytoezichthouder in
              dat land.
            </Text>

            {/* Contact */}
            <Heading as="h2" size="lg">
              Contact
            </Heading>
            <Text mb={0}>
              Voor vragen of verzoeken met betrekking tot de bescherming van uw
              persoonsgegevens, of over deze Verklaring in het algemeen, kunt u
              contact met ons opnemen via:
            </Text>
            <Text>E-mail: info@nomad360.io</Text>
          </VStack>
        </Box>

        {/* Footer & BackToTop */}
        <JoinCommunitySection />
        <Footer />
        <BackToTopButton />
      </Box>
    </>
  );
};

export default PrivacyPolicy;
